import { Box, useMantineTheme } from '@mantine/core';
import { TooltipWhite } from '@root/Design/Primitives';
import { SettingsLabelIcon } from '@root/Design/Settings';
import { ReactNode, useCallback, useMemo } from 'react';
import { Picker } from '../Picker/Picker';
import { iterateRtText, RichText, RichTextItem } from '../Text/RichText';
import { FormSection } from './SettingsForms';

interface IPresetBehaviorArgs<TData, TContext = null> {
    data: TData;
    context: TContext;
    updateContext: TContext extends null ? never : (context: TContext) => void;
}

export interface IPresetBehavior<TData, TInitializedData, TContext = never> {
    initialize: (args: IPresetBehaviorArgs<TData, TContext>) => TInitializedData;
    getForm: (args: IPresetBehaviorArgs<TInitializedData, TContext>) => Array<FormSection>;
    validate: (args: IPresetBehaviorArgs<TInitializedData, TContext>) => Array<string> | undefined;
}

export class PresetLookup<TOption extends IPresetOption> {
    private readonly itemLookup = new Map<string, TOption>();
    private readonly nameLookup = new Map<IPresetItem, string>();
    public constructor(items: Array<TOption | IPresetOptionGroup>) {
        this.itemLookup = new Map<string, TOption>();
        this.nameLookup = new Map<IPresetItem, string>();
        const collect = (item: IPresetItem) => {
            const text = [...iterateRtText(item.label, true)].join(' ');
            this.nameLookup.set(item, text);
            if ('id' in item) {
                this.itemLookup.set(item.id, item as TOption);
            } else {
                const children = item.children ?? [];
                children.forEach(collect);
            }
        };
        items.forEach(collect);
    }

    public getItem(id: string) {
        return this.itemLookup.get(id);
    }
    public getName(item: IPresetItem) {
        return this.nameLookup.get(item);
    }
}

export interface IPresetOption<TData = any, TInitializedData = any, TContext = any> {
    id: string;
    icon?: ReactNode;
    label: RichTextItem;
    description?: string;
    behavior: IPresetBehavior<TData, TInitializedData, TContext>;
}
export interface IPresetOptionGroup {
    label: RichTextItem;
    icon?: ReactNode;
    description?: string;
    children?: Array<IPresetItem | IPresetOptionGroup>;
}
export type IPresetItem<TData = any, TInitializedData = TData, TContext = any> =
    | IPresetOption<TData, TInitializedData, TContext>
    | IPresetOptionGroup;

interface IPresetPickerProps {
    items: Array<IPresetItem>;
    onChange: (item: null | IPresetOption) => void;
    selectedId?: string;
}
export function PresetPicker(props: IPresetPickerProps) {
    const { items, onChange, selectedId } = props;
    const itemLookup = useMemo(() => new PresetLookup(items), [items]);
    const nameAccessor = useCallback((item: IPresetItem) => itemLookup.getName(item) ?? '', [itemLookup]);
    const selectedItems = useMemo(() => {
        return !selectedId ? [] : ([itemLookup.getItem(selectedId)].filter((v) => !!v) as IPresetItem[]);
    }, [selectedId, itemLookup]);
    const handleChange = useCallback(
        (selection: IPresetItem[]) => {
            const [item] = selection;
            const option = 'id' in item ? item : null;
            onChange(option ? option : null);
        },
        [onChange]
    );
    const itemRenderer = useCallback((item: IPresetItem) => <PresetOptionLabel item={item} />, []);

    return (
        <Picker
            width="100%"
            items={items}
            renderItem={itemRenderer}
            nameAccessor={nameAccessor}
            selections={selectedItems}
            onChange={handleChange}
            mode="single"
            isSelectable={(item) => 'id' in item}
            minimizeHeight
            height={300}
        />
    );
}

function PresetOptionLabel({ item }: { item: IPresetItem }) {
    const { label, icon, description } = item;

    const labelEl = (
        <span style={{ whiteSpace: 'nowrap' }}>
            {icon ? <SettingsLabelIcon transform="translate(-3px, 0)">{icon}</SettingsLabelIcon> : null}
            <RichText text={label} />
        </span>
    );

    return description ? (
        <TooltipWhite openDelay={300} withinPortal multiline position={'right'} width={300} label={description}>
            <span>{labelEl}</span>
        </TooltipWhite>
    ) : (
        labelEl
    );
}
